import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { detectMobileOs } from '@/common/appStores';
import {
    createImpressionBatcher,
    createSession,
    createUiEventQueue,
    randomId,
    type UiEvent,
    type UiTarget,
} from '@/common/uiEventQueue';
import { UI_EVENT, detailEntrySource, screenOfPath } from '@/common/uiEventNames';
import { createScreenTimer } from '@/common/screenTime';

/**
 * 자체 경량 행동 분석 — 이벤트를 백엔드(/api/events)로 전송.
 *
 * navigator.sendBeacon을 사용해 페이지 이탈 중에도 유실 없이 보낸다(쿠키는 same-origin 자동 포함 →
 * 로그인 유저는 MAC 쿠키, 비로그인은 localStorage `mc_device_id` 로 서버가 유니크 식별). 수집 실패는 조용히 무시한다.
 *
 * 두 갈래(docs/unified-log-plan.md 3.1):
 * - 방문 원장(`trackEvent`): page_view·상세 보기·가입·결제·앱 설치 유도 — 지금처럼 즉시 1건(`/api/events`).
 * - 화면 행동 기록(`track.*`): 노출·클릭·검색·목록 조건·AI 말풍선·지원 — 큐에 모아 `/api/events/batch` 로 묶어 보낸다.
 *   서버 스위치(어드민 기능 공개 `ui_events`, `/api/events/config`)가 꺼져 있으면 **아무것도 쌓거나 보내지 않는다**.
 */

type EventPayload = {
    path?: string;
    targetId?: string;
    referrer?: string;
    /** 상세 보기에 들어온 경로(DETAIL_SOURCE) — 상세 보기 종류만 */
    source?: string;
};

const ENDPOINT = '/api/events';
const DEVICE_KEY = 'mc_device_id';
const UTM_KEY = 'mc_utm';

type Utm = { utmSource?: string; utmMedium?: string; utmCampaign?: string; utmContent?: string };

/**
 * 랜딩 URL 쿼리에서 UTM을 1회 캡처해 세션 동안 유지한다(first-touch).
 * 첫 진입에 utm_*가 있으면 sessionStorage에 저장하고, 이후 내부 이동(utm 없는 URL)에서도 같은 유입으로 귀속한다.
 */
function captureUtm(): Utm {
    try {
        const params = new URLSearchParams(window.location.search);
        const source = params.get('utm_source') || undefined;
        if (source) {
            const utm: Utm = {
                utmSource: source,
                utmMedium: params.get('utm_medium') || undefined,
                utmCampaign: params.get('utm_campaign') || undefined,
                utmContent: params.get('utm_content') || undefined,
            };
            sessionStorage.setItem(UTM_KEY, JSON.stringify(utm));
            return utm;
        }
        const saved = sessionStorage.getItem(UTM_KEY);
        return saved ? (JSON.parse(saved) as Utm) : {};
    } catch {
        return {};
    }
}

let memoryDeviceId: string | null = null;

function uuid(): string {
    try {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    } catch { /* ignore */ }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * 비로그인 유니크 식별용 device_id를 클라이언트에서 생성·보관한다.
 * 서버가 발급하면 첫 페이지의 동시 이벤트에서 쿠키 경쟁으로 id가 쪼개지므로(첫 방문자 중복 카운트),
 * 클라이언트 localStorage에 한 번 만들어 고정한다(GA client_id와 동일 방식).
 */
function getDeviceId(): string {
    try {
        let id = localStorage.getItem(DEVICE_KEY);
        if (!id) {
            id = uuid();
            localStorage.setItem(DEVICE_KEY, id);
        }
        return id;
    } catch {
        // localStorage 불가(프라이빗 모드 등) → 세션 메모리 id로 폴백
        if (!memoryDeviceId) memoryDeviceId = uuid();
        return memoryDeviceId;
    }
}

type DeviceHint = { appPlatform?: string; clientOs?: string };

/**
 * 기기 구분 힌트 — 서버가 User-Agent 로 PC·모바일 웹을 가르고, UA 로 알 수 없는 두 가지만 여기서 보탠다.
 * - appPlatform: 명치사 앱(Capacitor 웹뷰) 안이면 'ios'/'android'. 앱이 운영 웹을 띄우므로 웹 배포만으로 모든 앱 버전에 적용된다.
 * - clientOs: iPadOS 13+ 는 데스크톱 UA 를 보내므로 터치포인트로 보정한 OS (서버는 UA 가 Mac 일 때만 이 값을 쓴다).
 */
function deviceHint(): DeviceHint {
    try {
        const hint: DeviceHint = { clientOs: detectMobileOs() };
        if (Capacitor.isNativePlatform()) {
            hint.appPlatform = Capacitor.getPlatform();
        }
        return hint;
    } catch {
        return {};
    }
}

export function trackEvent(type: string, data?: EventPayload): void {
    try {
        const body = JSON.stringify({ type, deviceId: getDeviceId(), ...captureUtm(), ...deviceHint(), ...data });
        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
            const blob = new Blob([body], { type: 'application/json' });
            navigator.sendBeacon(ENDPOINT, blob);
            return;
        }
        // sendBeacon 미지원 폴백
        fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true,
            credentials: 'include',
        }).catch(() => { /* 무시 */ });
    } catch {
        /* 분석 실패는 UX에 영향 주지 않음 */
    }
}

export function trackPageView(path: string): void {
    trackEvent('PAGE_VIEW', { path, referrer: document.referrer || undefined });
}

/** 푸시 알림을 누른 뒤 이 시간 안에 옮긴 화면을 '푸시로 들어옴'으로 본다 */
const PUSH_ENTRY_WINDOW_MS = 5_000;
let pushEntryAt = 0;

/** 푸시 알림을 눌러 화면을 옮기기 직전에 부른다(capacitorApp) — 다음 화면의 상세 보기 출처가 push 가 된다 */
export function markPushEntry(): void {
    pushEntryAt = Date.now();
}

/** 이 문서 안 화면 이동 — 지금·바로 앞 경로, 푸시로 옮겼는지. 같은 경로로 다시 불리면(개발 모드 두 번 실행 등) 그대로 둔다 */
let route: { current: string | null; previous: string | null; fromPush: boolean } = { current: null, previous: null, fromPush: false };

const enterRoute = (pathname: string): typeof route => {
    if (route.current === pathname) return route;
    const fromPush = pushEntryAt > 0 && Date.now() - pushEntryAt < PUSH_ENTRY_WINDOW_MS;
    pushEntryAt = 0;
    route = { current: pathname, previous: route.current, fromPush };
    return route;
};

const entrySourceOf = (pathname: string): string | undefined => {
    try {
        const entered = enterRoute(pathname);
        return detailEntrySource({
            detailPath: pathname,
            previousPath: entered.previous,
            fromPush: entered.fromPush,
            landingSearch: entered.previous == null ? window.location.search : '',
            referrer: document.referrer || '',
            origin: window.location.origin,
        });
    } catch {
        return undefined;
    }
};

/**
 * 경로가 상세 페이지면 콘텐츠 조회 이벤트를 함께 전송한다(리스트 클릭이 아니라 진입 자체 — 검색·딥링크 유입 포함).
 * 반환 이벤트의 targetId로 인기 글/공고/세미나/전문가글을 집계한다. 들어온 경로(source: list·home·search·push·share·link·direct·other)를
 * 함께 보낸다 — 기능 지표 단계별 흐름이 목록에서 들어온 상세 보기만 목록 다음 단계로 센다.
 * 라우트가 바뀔 때마다 부른다(상세가 아니어도) — 바로 앞 화면을 기억해야 하므로.
 */
export function trackDetailView(pathname: string): void {
    const source: string | undefined = entrySourceOf(pathname);
    // [정규식, 이벤트타입] — 목록/신규(new)·하위경로 제외를 위해 세그먼트 1개만 매칭
    const rules: Array<[RegExp, string]> = [
        [/^\/posts\/([^/]+)$/, 'POST_VIEW'],
        [/^\/recruitments\/([^/]+)$/, 'RECRUITMENT_VIEW'],
        [/^\/seminars\/([^/]+)$/, 'SEMINAR_VIEW'],
        [/^\/expert-lounge\/([^/]+)$/, 'EXPERT_VIEW'],
    ];
    for (const [re, type] of rules) {
        const m = pathname.match(re);
        if (m) {
            const id = m[1];
            // 'new' 등 상세가 아닌 값은 제외 (숫자 또는 24자리 hex ObjectId만 허용)
            if (/^\d+$/.test(id) || /^[a-f0-9]{24}$/i.test(id)) {
                trackEvent(type, { targetId: id, source });
            }
            return;
        }
    }
}

// ─────────────────────────── 화면 행동 기록(노출·클릭 등) ───────────────────────────

const BATCH_ENDPOINT = '/api/events/batch';
const CONFIG_ENDPOINT = '/api/events/config';
/** 수집 스위치를 다시 묻는 주기 */
const CONFIG_TTL_MS = 5 * 60 * 1000;
/** 빌드 상수로 끄기 — 서버 스위치와 상관없이 이 빌드는 보내지 않는다 */
const BUILD_DISABLED: boolean = import.meta.env.VITE_UI_EVENTS_DISABLED === '1';

const safeStorage = (kind: 'local' | 'session'): Storage | null => {
    try {
        return kind === 'local' ? window.localStorage : window.sessionStorage;
    } catch {
        return null;
    }
};

let appVersion: string | undefined;
let webBuild: string | undefined;

/** 화면 빌드 해시 — 운영 빌드의 진입 스크립트 이름(assets/index-{해시}.js)에서. 개발 서버는 없음 */
const detectWebBuild = (): string | undefined => {
    try {
        const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'));
        for (const script of scripts) {
            const match = script.src.match(/assets\/index-([A-Za-z0-9_-]{4,40})\.js/);
            if (match) return match[1];
        }
    } catch { /* ignore */ }
    return undefined;
};

const sendBatch = (body: string, beacon: boolean): boolean | Promise<boolean> => {
    if (beacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
        try {
            return navigator.sendBeacon(BATCH_ENDPOINT, new Blob([body], { type: 'application/json' }));
        } catch {
            return false;
        }
    }
    return fetch(BATCH_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: body.length < 60_000,
        credentials: 'include',
    })
        // 413(너무 큼)은 다시 보내도 같으므로 보낸 것으로 친다
        .then((response) => response.ok || response.status === 413)
        .catch(() => false);
};

const uiQueue = createUiEventQueue({
    send: sendBatch,
    meta: () => ({ deviceId: getDeviceId(), ...deviceHint(), appVersion, webBuild }),
    store: typeof window !== 'undefined' ? safeStorage('local') : null,
    isOnline: () => typeof navigator === 'undefined' || navigator.onLine !== false,
});

const nextSessionId = createSession(typeof window !== 'undefined' ? safeStorage('session') : null);

let uiEnabled = false;
let configViewer: string | null = null;
let configFetchedAt = 0;
let configInFlight: Promise<void> | null = null;
let lifecycleAttached = false;

const attachLifecycle = (): void => {
    if (lifecycleAttached || typeof window === 'undefined') return;
    lifecycleAttached = true;
    webBuild = detectWebBuild();
    // 화면이 숨겨지거나 닫힐 때 비운다(sendBeacon) — 머문 시간을 먼저 마감해 같은 묶음에 싣는다
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            screenTimer.hide();
            impressionBatcher.drain();
            void uiQueue.flush(true);
        } else if (document.visibilityState === 'visible') {
            screenTimer.show();
        }
    });
    window.addEventListener('pagehide', () => {
        screenTimer.hide();
        impressionBatcher.drain();
        void uiQueue.flush(true);
    });
    // 온라인 복귀 때 오프라인 큐를 보낸다
    window.addEventListener('online', () => {
        if (uiEnabled) void uiQueue.flush(false);
    });
    if (Capacitor.isNativePlatform()) {
        // 웹뷰는 백그라운드 전환 때 visibilitychange 가 늦거나 빠질 수 있다
        void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
            if (!isActive) {
                screenTimer.hide();
                impressionBatcher.drain();
                void uiQueue.flush(true);
            } else {
                screenTimer.show();
            }
        });
        CapacitorApp.getInfo()
            .then((info) => { appVersion = `${info.version}+${info.build}`.replace(/[^A-Za-z0-9_.+-]/g, ''); })
            .catch(() => { /* 무시 */ });
    }
};

const loadConfig = (viewer: string): Promise<void> => {
    if (configInFlight) return configInFlight;
    const promise = fetch(CONFIG_ENDPOINT, { credentials: 'include' })
        .then((response) => (response.ok ? response.json() : null))
        .then((json: { result?: { enabled?: boolean } } | null) => {
            if (configViewer !== viewer) return;
            const wasEnabled = uiEnabled;
            uiEnabled = json?.result?.enabled === true;
            if (uiEnabled && !wasEnabled) enabledListeners.forEach((listener) => listener());
            configFetchedAt = Date.now();
            if (uiEnabled) void uiQueue.flush(false); // 지난 실행에서 남은 오프라인 큐
            else uiQueue.clear();
        })
        .catch(() => {
            // 실패 — 이전 값을 그대로 두고 다음 기록 때 다시 묻는다
            configFetchedAt = 0;
        })
        .finally(() => {
            configInFlight = null;
        });
    configInFlight = promise;
    return promise;
};

const enabledListeners = new Set<() => void>();

/** 수집이 꺼짐→켜짐으로 바뀔 때 부를 함수 — 켜지기 전에 화면에 있던 카드를 다시 세려고(useUiScope). 돌려준 함수로 해제 */
export function onUiEventsEnabled(listener: () => void): () => void {
    enabledListeners.add(listener);
    return () => {
        enabledListeners.delete(listener);
    };
}

/**
 * 지금 보는 사람이 바뀔 때(로그인·로그아웃·계정 전환) 부른다 — 수집 스위치를 이 사람 기준으로 다시 묻는다.
 * @param viewer 비로그인 'anon', 로그인 회원번호, 프로필 받기 전 'pending'
 */
export function setUiEventViewer(viewer: string): void {
    if (BUILD_DISABLED || typeof window === 'undefined' || viewer === 'pending') return;
    attachLifecycle();
    if (configViewer === viewer && Date.now() - configFetchedAt < CONFIG_TTL_MS) return;
    const changed = configViewer !== viewer;
    configViewer = viewer;
    if (changed) {
        // 다른 사람으로 바뀌면 앞 사람 것을 이 사람 묶음에 섞지 않도록 먼저 보낸다(쿠키가 이미 바뀌었을 수 있어 꺼 둔 채로 비움)
        impressionBatcher.drain();
        if (uiEnabled) void uiQueue.flush(true);
        uiEnabled = false;
        configInFlight = null;
    }
    void loadConfig(viewer);
}

/** 수집이 켜져 있는지(화면 쪽 판정 — 테스트·점검용) */
export function isUiEventsEnabled(): boolean {
    return uiEnabled && !BUILD_DISABLED;
}

const currentPath = (): string | undefined => {
    try {
        return window.location.pathname;
    } catch {
        return undefined;
    }
};

type UiProps = NonNullable<UiEvent['props']>;

/** 화면 행동 기록 공통 칸 */
export interface UiContext {
    screen?: string;
    section?: string;
    sectionType?: string;
    listKey?: string;
    pos?: number;
}

const push = (type: string, fields: Omit<UiEvent, 'eid' | 'type' | 'ts'>): void => {
    if (!uiEnabled || BUILD_DISABLED) return;
    try {
        if (configViewer !== null && Date.now() - configFetchedAt > CONFIG_TTL_MS) void loadConfig(configViewer);
        uiQueue.push({ eid: randomId(), type, ts: Date.now(), sid: nextSessionId(), path: currentPath(), ...fields });
    } catch {
        /* 분석 실패는 UX에 영향 주지 않음 */
    }
};

const impressionBatcher = createImpressionBatcher((group) => {
    push(UI_EVENT.IMPRESSION, { screen: group.screen, section: group.section, sectionType: group.sectionType, listKey: group.listKey, targets: group.targets });
});

/**
 * 화면 머문 시간 — 화면(라우트)을 옮기거나 앱·탭이 가려질 때 그 화면이 보인 시간(초)을 한 건 낸다(가려진 시간 제외,
 * 1초 미만 버림, 최대 30분). path 는 그 화면의 경로(쿼리 없음). 수집이 꺼져 있으면 push 가 버린다.
 */
const screenTimer = createScreenTimer({
    screenOf: screenOfPath,
    emit: ({ screen, path, seconds }) => {
        push(UI_EVENT.SCREEN_TIME, { screen, path, props: { screen, seconds } });
    },
    visible: () => typeof document === 'undefined' || document.visibilityState === 'visible',
});

/** 라우트가 바뀔 때 부른다(PageViewTracker) — 앞 화면 머문 시간을 마감하고 새 화면을 잰다 */
export function trackScreenEnter(pathname: string): void {
    if (BUILD_DISABLED || typeof window === 'undefined') return;
    try {
        screenTimer.enter(pathname);
    } catch { /* 분석 실패는 UX에 영향 주지 않음 */ }
}

/**
 * 화면 행동 기록 — 화면 코드는 이 얇은 함수만 쓴다. 스위치가 꺼져 있으면 아무 일도 하지 않는다.
 * 글자 원문(검색어·질문·입력값)은 절대 싣지 않는다.
 */
export const track = {
    /** 노출 한 건 — 1초 단위로 같은 목록 표시끼리 모아 노출 묶음 한 건으로 보낸다(중복 제거는 useUiScope 가 한다) */
    impression(context: UiContext, target: UiTarget): void {
        if (!uiEnabled || BUILD_DISABLED) return;
        impressionBatcher.add({ screen: context.screen, section: context.section, sectionType: context.sectionType, listKey: context.listKey }, target);
    },
    click(context: UiContext, target: UiTarget, props?: UiProps): void {
        push(UI_EVENT.CLICK, { ...context, target, pos: context.pos ?? target.pos, props });
    },
    /** 검색 — 영역·결과 수·검색어 길이만 */
    search(context: UiContext, props: { area: string; resultCount?: number; queryLength?: number }): void {
        push(UI_EVENT.SEARCH, { ...context, props });
    },
    filterApply(context: UiContext, props: UiProps): void {
        push(UI_EVENT.FILTER_APPLY, { ...context, props });
    },
    aiHookView(context: UiContext, props: UiProps): void {
        push(UI_EVENT.AI_HOOK_VIEW, { ...context, props });
    },
    aiChipClick(context: UiContext, target: UiTarget, props?: UiProps): void {
        push(UI_EVENT.AI_CHIP_CLICK, { ...context, target, props });
    },
    aiQuestionSent(context: UiContext, props: UiProps): void {
        push(UI_EVENT.AI_QUESTION_SENT, { ...context, props });
    },
    applyStart(context: UiContext, recruitmentId: string | number, props?: UiProps): void {
        push(UI_EVENT.APPLY_START, { ...context, target: { kind: 'recruitment', id: String(recruitmentId) }, props });
    },
    applySubmit(context: UiContext, recruitmentId: string | number, props?: UiProps): void {
        push(UI_EVENT.APPLY_SUBMIT, { ...context, target: { kind: 'recruitment', id: String(recruitmentId) }, props });
    },
    /** 세미나 결제 완료 — 금액은 구간(amountBand)만, 원값 금지 */
    payDone(context: UiContext, seminarId: string, amountBand: string): void {
        push(UI_EVENT.PAY_DONE, { ...context, target: { kind: 'seminar', id: seminarId }, props: { amountBand } });
    },
};
